/**
 * Jazz-backed implementation of the `OpenProject` port. Wraps a loaded
 * `DiagramProject` CoMap plus the Jazz Group that owns it, and hides every
 * `$jazz` access behind the port's methods.
 *
 * Snapshot caching exists to satisfy the port's reactivity contract:
 * `getSnapshot` returns the same reference until a value actually changes,
 * so `useSyncExternalStore` does not loop.
 */
import { type co, type Group } from "jazz-tools";
import type { CollaborativeTextProvider, FileStore } from "@diagram/editor";
import type { AnnotationsBackend } from "@diagram/draw-overlay";
import type { DiagramProject, VibeDiagramAccount } from "./schema";
import type {
  OpenProject,
  OpenProjectSnapshot,
  PinnedDeployment,
} from "../stores/OpenProject";
import type { Unsubscribe } from "../stores/ProjectStore";
import { JazzProjectStore, toRole } from "../stores/JazzProjectStore";
import { JazzFileStoreAdapter } from "./JazzFileStoreAdapter";
import { JazzAnnotationsBackend } from "../annotations/JazzAnnotationsBackend";
import { createAnnotations } from "../hooks/useJazzDB";

/**
 * The project shape this adapter is built from — deeply resolved down to
 * strokes and cursors, matching the resolve query in `useJazzOpenProject`.
 * `ensureAnnotations` assigns a freshly-created `Annotations` CoMap, which
 * only type-checks against the resolved shape.
 */
type LoadedProject = co.loaded<
  typeof DiagramProject,
  {
    annotations: { strokes: { $each: true }; cursors: true };
    files: {
      $each: { content: true; updates: { $each: true; $onError: null } };
    };
  }
>;
type LoadedAccount = co.loaded<typeof VibeDiagramAccount>;

/**
 * Caches that must outlive a single adapter instance.
 *
 * `useCoState` mints a **new** CoValue wrapper on every notification, so the
 * hook rebuilds `JazzOpenProject` whenever anything on the project changes —
 * including the `updatedAt` stamp that every keystroke writes. Per-instance
 * caching therefore buys nothing across exactly the churn it was meant to
 * absorb. Keying on the underlying CoValue identity instead reproduces what
 * the `useMemo(..., [document.annotations])` and `useSyncExternalStore`
 * contracts assumed:
 *
 * - the annotations backend survives project writes, so the drawing overlay
 *   keeps its Jazz subscription and stroke cache instead of tearing them down
 *   on every keystroke;
 * - `getSnapshot()` returns the same reference when nothing it exposes moved,
 *   which is what `useSyncExternalStore` compares by identity.
 *
 * The same reasoning applies with more force to the file store since it
 * became the collaborative-text provider: it owns a Yjs document and a log
 * subscription per open file, and the editor binds CodeMirror to a `Y.Text`
 * it reads once on mount. Rebuilding the adapter per keystroke would leak a
 * document per keystroke and leave the editor bound to an orphan.
 *
 * The annotations map is keyed on a CoValue and is weak. The other two are
 * keyed by id — bounded by the projects visited in one session.
 */
const annotationsBackends = new WeakMap<object, AnnotationsBackend>();
const fileStores = new Map<string, JazzFileStoreAdapter>();
const lastSnapshots = new Map<string, OpenProjectSnapshot>();

/** Shallow equality over the snapshot's flat fields. */
function sameSnapshot(a: OpenProjectSnapshot, b: OpenProjectSnapshot): boolean {
  return (
    a.id === b.id &&
    a.title === b.title &&
    a.role === b.role &&
    a.isPublic === b.isPublic &&
    a.hasAnnotations === b.hasAnnotations &&
    a.pinnedDeployment?.id === b.pinnedDeployment?.id &&
    a.pinnedDeployment?.url === b.pinnedDeployment?.url
  );
}

/**
 * The file store for a project, reused across adapter rebuilds.
 *
 * Keyed on the `files` CoList's **id**, not the wrapper. Keying on the
 * wrapper looks right — it is what the adapter reads — but Jazz mints a fresh
 * wrapper for every value in a resolve tree whenever any of them changes, and
 * this project's query now resolves `files → $each → updates → $each`. So
 * appending a single `TextUpdate` — one keystroke batch — remints the update
 * list, the file entry, the `files` list and the project alike, and a
 * wrapper-keyed cache misses on every one of them. That is exactly the leak
 * the cache exists to prevent: a `Y.Doc`, a Jazz subscription and a flush
 * timer per file, per edit, none of them ever released.
 *
 * The id is stable, so the adapter survives. It is handed the newest wrapper
 * on the way through, because the stale one it was built with is not a
 * reliable view of the project.
 */
function fileStoreFor(
  project: LoadedProject,
  group: Group,
): JazzFileStoreAdapter {
  const id = project.files.$jazz.id;
  const existing = fileStores.get(id);
  if (existing) {
    existing.adoptProject(project);
    return existing;
  }
  const store = new JazzFileStoreAdapter(project, group);
  fileStores.set(id, store);
  return store;
}

export class JazzOpenProject implements OpenProject {
  private readonly group: Group;
  private readonly fileStore: JazzFileStoreAdapter;
  private snapshot: OpenProjectSnapshot;
  private readonly listeners = new Set<() => void>();
  private unsubscribeJazz: (() => void) | null = null;

  constructor(
    private readonly project: LoadedProject,
    private readonly account: LoadedAccount,
  ) {
    this.group = project.$jazz.owner;
    this.fileStore = fileStoreFor(project, this.group);
    this.snapshot = this.stabilize(this.readSnapshot());
  }

  private readSnapshot(): OpenProjectSnapshot {
    const everyoneRole = this.group.getRoleOf("everyone");
    const pinnedId = this.project.pinnedDeploymentId;
    return {
      id: this.project.$jazz.id,
      title: this.project.title,
      role: toRole(this.group.myRole()),
      isPublic: everyoneRole === "reader" || everyoneRole === "writer",
      pinnedDeployment: pinnedId
        ? { id: pinnedId, url: this.project.pinnedDeploymentUrl ?? undefined }
        : undefined,
      hasAnnotations: Boolean(this.project.annotations),
    };
  }

  /**
   * Return the previously-published snapshot when nothing this adapter
   * exposes has moved, so the reference stays stable across the adapter
   * rebuilds `useCoState` forces on every project write.
   */
  private stabilize(next: OpenProjectSnapshot): OpenProjectSnapshot {
    const previous = lastSnapshots.get(next.id);
    if (previous && sameSnapshot(previous, next)) return previous;
    lastSnapshots.set(next.id, next);
    return next;
  }

  /**
   * Recompute the snapshot and notify listeners only when something moved.
   * Called after every local write and on every Jazz notification, so the
   * component sees writes without waiting for the sync round-trip.
   */
  private refresh(): void {
    const next = this.stabilize(this.readSnapshot());
    if (next === this.snapshot) return;
    this.snapshot = next;
    for (const listener of this.listeners) listener();
  }

  private canWrite(): boolean {
    const role = this.snapshot.role;
    return role === "admin" || role === "writer";
  }

  private touch(): void {
    this.project.$jazz.set("updatedAt", new Date().toISOString());
  }

  getSnapshot(): OpenProjectSnapshot {
    return this.snapshot;
  }

  subscribe(cb: () => void): Unsubscribe {
    this.listeners.add(cb);
    this.unsubscribeJazz ??= this.project.$jazz.subscribe({}, () => {
      this.refresh();
    });
    return () => {
      this.listeners.delete(cb);
      if (this.listeners.size > 0) return;
      this.unsubscribeJazz?.();
      this.unsubscribeJazz = null;
    };
  }

  getFileStore(): FileStore {
    return this.fileStore;
  }

  /**
   * The same adapter: it serves both ports, because both read the one
   * `FileEntry` list and a file's two representations have to stay in step.
   */
  getCollaborativeText(): CollaborativeTextProvider {
    return this.fileStore;
  }

  getAnnotationsBackend(): AnnotationsBackend | null {
    const annotations = this.project.annotations;
    if (!annotations) return null;
    let backend = annotationsBackends.get(annotations);
    if (!backend) {
      backend = new JazzAnnotationsBackend(annotations, this.account);
      annotationsBackends.set(annotations, backend);
    }
    return backend;
  }

  setTitle(title: string): Promise<void> {
    if (!this.canWrite()) return Promise.resolve();
    this.project.$jazz.set("title", title);
    this.touch();
    this.refresh();
    return Promise.resolve();
  }

  setPublic(isPublic: boolean): Promise<void> {
    if (this.snapshot.role !== "admin") return Promise.resolve();
    if (isPublic) {
      this.group.addMember("everyone", "reader");
    } else {
      this.group.removeMember("everyone");
    }
    this.refresh();
    return Promise.resolve();
  }

  pinDeployment(deployment: PinnedDeployment): Promise<void> {
    if (!this.canWrite()) return Promise.resolve();
    if (this.snapshot.pinnedDeployment?.id === deployment.id) {
      return Promise.resolve();
    }
    this.project.$jazz.set("pinnedDeploymentId", deployment.id);
    if (deployment.url !== undefined) {
      this.project.$jazz.set("pinnedDeploymentUrl", deployment.url);
    }
    this.refresh();
    return Promise.resolve();
  }

  /**
   * Two simultaneous writers may both create an Annotations CoMap; LWW
   * resolves it and one is orphaned. Documented as acceptable in
   * `plans/drawing-overlay/plan.md`.
   */
  ensureAnnotations(): Promise<void> {
    if (!this.canWrite()) return Promise.resolve();
    if (this.project.annotations) return Promise.resolve();
    this.project.$jazz.set("annotations", createAnnotations());
    this.touch();
    this.refresh();
    return Promise.resolve();
  }

  /**
   * `async` rather than `Promise.resolve(...)`: `forkProject` is synchronous
   * and throws when a file's content has not synced yet, and an argument
   * expression throws *before* `Promise.resolve` can wrap it. That escaped
   * the caller's `.catch`/`.finally` and left the Fork button stuck.
   */
  // eslint-disable-next-line @typescript-eslint/require-await -- async is what converts the sync throw into a rejection
  async fork(): Promise<string | null> {
    const store = new JazzProjectStore(this.account);
    return store.forkProject(this.project.$jazz.id, this.project);
  }
}
